import { ChangeDetectionStrategy, Component, computed, ViewEncapsulation } from '@angular/core';
import { Chart, ChartScrollablePlotAreaOptions, OptionsStackingValue, PlotBarOptions, PlotOptions, SeriesBarOptions, SVGDOMElement } from 'highcharts';
import { filterToDistinct } from 'src/app/shared/utils/filterToDistinct';
import { PbiChartTypes } from '../../../shared/chart/chart';
import { VisualFieldRoleType } from '../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../VisualFieldRoleTypeMap';
import { FontSize } from '../VisualStyle';
import { ChartPoint, ChartVisual, ChartVisualComponent } from './ChartVisual.component';
import { SortDirection } from 'src/app/services/api.services';
import { VisualField, VisualFieldSortingInit } from '../VisualField';

const BAR_PADDING    = 4;
const COLUMN_PADDING = 4;

@Component({
  selector: 'pbir-visual__barChart',
  templateUrl: './ChartVisual.component.html',
  styleUrls: ['./ChartVisual.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BarChartVisualComponent
  extends ChartVisualComponent<
    SeriesBarOptions,
    BarChartVisual.Options,
    BarChartVisual.State,
    BarChartVisual.Style> {
  get measuresRoleType() { return BarChartVisual.roles.measures; }
  get groupingRoleType() { return BarChartVisual.roles.axis; }
  get legendRoleType() { return BarChartVisual.roles.legend; }

  readonly direction   = computed(() => this.options().direction);
  readonly displayType = computed(() => this.options().displayType);

  readonly bars    = computed(() => this.direction() == BarChartVisual.BarDirection.Horizontal);
  readonly columns = computed(() => this.direction() == BarChartVisual.BarDirection.Vertical);

  readonly simple    = computed(() => this.displayType() == BarChartVisual.DisplayType.Simple);
  readonly clustered = computed(() => this.displayType() == BarChartVisual.DisplayType.Clustered
                                   && ((this.measureFields().length > 1)
                                    || (this.groupingFields().length > 0 && this.legendFields().length > 0)));

  readonly stacked     = computed(() => this.displayType() == BarChartVisual.DisplayType.Stacked);
  readonly stacked100p = computed(() => this.displayType() == BarChartVisual.DisplayType.Stacked100p);

  get chartType() {
    return this.bars() ? 'bar' : 'column';
  }

  computePlotOptions(): PlotOptions {
    let stacking: OptionsStackingValue =
      (this.clustered())
        ? undefined
        : (this.stacked100p())
          ? 'percent'
          : 'normal';

    let chartOptions: Partial<PlotBarOptions> = {
      animation: !this.disableAnimations,

      stacking,

      colorByPoint: false,
      inactiveOtherPoints: true,
      //cropThreshold: 0,
      //boostThreshold: 0,
      getExtremesFromAll: true,
      centerInCategory: true,
      
      grouping:     true,
      pointWidth:   undefined,
      groupPadding: 0.1,
      pointPadding: this.clustered() ? 0 : 0.1,

      nullInteraction: false,

      borderRadius: 0,
      borderWidth:  0,
    };

    let barSize = this.barSize();
    let barPadding = this.barPadding();
    let scrollViewSize = this.scrollViewSize();

    if (this.scrollActive()) { 
      chartOptions = {
        ...chartOptions,
        pointWidth:   barSize,
        groupPadding: 0,
      };
    }
    else {
      let space            = scrollViewSize;
      let categoryCount    = this.totalCategories();
      let spacePerCategory = space / categoryCount;

      // paddingPx = padding   * (space / categoryCount)
      // padding   = paddingPx / (space / categoryCount)
      let groupPadding = 4 /  spacePerCategory;

      chartOptions = {
        ...chartOptions,
        groupPadding
      };
    }

    return {
      ...super.computePlotOptions(),
      
      bar:    chartOptions,
      column: chartOptions,
    };
  }

  computeNavigatorOptions(): Highcharts.NavigatorOptions {
    let options = super.computeNavigatorOptions();

    return {
      ...options,
      adaptToUpdatedData: true,
    };
  }

  computeScrollablePlotArea() {
    let _ = [this.scrollViewSize(), this.totalSize()];

    let area = {
      scrollPositionX: 0,
      scrollPositionY: 0,
      minWidth:  undefined,
      minHeight: undefined,
      opacity:   0,
      isDirty:   true,
    };

    if (this.isHorizontalPlot()) {
      area.minHeight = 1;
    }
    else {
      area.minWidth = 1;
    }

    if (this.scrollActive()) {
      let minSize = this.totalSize();

      area.opacity = 0.85;

      if (this.isHorizontalPlot()) {
        area.minHeight = minSize;
        area.minWidth  = undefined;
      }
      else {
        area.minWidth  = minSize;
        area.minHeight = undefined;
      }
    }

    return area;
  }

  readonly scrollViewSize = computed(() => {
    let size = this.chart()?.getContainerSize();
    return (this.isHorizontalPlot() ? size.h : size.w);
  });
  
  readonly totalCategories = computed(() => {
    if (!this.rows()?.hasResult() || !this.groupingFields()?.length)
      return 1;

    let grouping      = this.groupingFields()[0];
    let groupingIndex = this.rows().table.columns.get(grouping).dataIndex;
    let groupings     = this.rows().result().rows.map((row) => row[groupingIndex]);

    return filterToDistinct(groupings).length;
  });

  readonly totalBars = computed(() => {
    if (!this.rows()?.hasResult() || !this.groupingFields()?.length)
      return 1;

    let barCount = this.totalCategories();

    if (this.clustered() &&
        this.groupingFields().length > 0 &&
        this.legendFields().length > 0) {
      let source           = this.rows();
      let groupingIndex    = source.table.columns.get(this.groupingFields()[0]).dataIndex;
      let legendIndex      = source.table.columns.get(this.legendFields()[0]).dataIndex;
      let measureIndex     = source.table.columns.get(this.measureFields()[0]).dataIndex;
      let itemsPerCategory = new Map<any, Set<any>>();

      let rows = source.result().rows;

      for (let i = 0; i < rows.length; ++i) {
        let row      = rows[i];
        let category = row[groupingIndex];
        let legend   = row[legendIndex];
        let value    = row[measureIndex];

        let set = itemsPerCategory.get(category);
        if (!set) {
          set = new Set();
          itemsPerCategory.set(category, set);
        }

        set.add(legend);
      }

      let maxBarsPerCategory = 0;

      for (let x of itemsPerCategory.values())
        maxBarsPerCategory = Math.max(maxBarsPerCategory, x.size);

      barCount *= maxBarsPerCategory;
    }

    barCount *= this.measureFields().length;

    return barCount;
  });

  readonly barPadding = computed(() => this.isHorizontalPlot() ? BAR_PADDING : COLUMN_PADDING);

  readonly barSize = computed(() => {
    return FontSize.value(this.style()?.xAxis()?.fontSize, 12);
  });

  readonly totalSize = computed(() => {
    let barPadding = this.barPadding();
    return Math.max(1, this.totalBars() * (this.barSize() * 2 + barPadding) - barPadding * 2);
  });

  readonly scrollActive = computed(() => {
    let availableSpace = this.scrollViewSize();
    let usedSpace      = this.totalSize();
    return usedSpace > availableSpace;
  });


  computeXAxis() {
    let axis = super.computeXAxis();

    axis = {
      ...axis,

      scrollbar: {
        enabled: false,
      },

      labels: {
        ...axis.labels,
        padding: 0,
        autoRotation: this.isHorizontalPlot() ? undefined : [-45, -60, -75, -90],
        allowOverlap: true,
        reserveSpace: true,
      },

      minPadding: 0,
      maxPadding: 0,

      uniqueNames: true,
      showEmpty:   true,

      events: {
        ...axis.events,
      },

      categories: this.computeCategorysForBarLikeChart()
    };

    return axis;
  }

  computeYAxis() {
    let axis = super.computeYAxis();
    
    axis = {
      ...axis,
      labels: {
        ...axis.labels,
        format: this.stacked100p() ? '{value}%' : undefined,
      },
    };

    return axis;
  }

  isHorizontalPlot() {
    return this.bars();
  }

  areDataLabelsInside() {
    return this.stacked100p();
  }

  useSingleSeriesStack() {
    return this.stacked() || this.stacked100p();
  }

  tooltipsIncludePercentage() {
    return this.stacked100p();
  }


  computeShowPercentageOfTotalInDataLabel() { return this.stacked100p(); }
  computeShowNameInDataLabel() { return false; }


  getAdditionalRightSpacing() {
    if (this.isHorizontalPlot())
      return 70;

    return 0;
  }


  computeSharedTooltips() {
    return !this.clustered() && (this.legendFields().length > 0 || this.measureFields().length > 1);
  }


 override applyDefaultSorting(fields: VisualField[]) {

    let axiesFieldIndex = fields.findIndex((f) => f.role().type.title == VisualFieldRoleType.Defaults.groupingAxis.title);
    if (axiesFieldIndex != -1) {
      let field = fields[axiesFieldIndex]

      fields[axiesFieldIndex] = new VisualField(
        field.target,
        field.visualFieldId,
        <VisualFieldSortingInit>{
          sorted: true,
          sortDirection: this.chartType == 'bar' ? SortDirection.Descending : SortDirection.Ascending,
          sortOrder: 0
        }
      );

      return fields;
    }

    return fields
  }
}

export namespace BarChartVisual {
  export enum BarDirection {
    Horizontal = 'horizontal',
    Vertical = 'vertical',
  }

  export enum DisplayType {
    Simple = 'simple',
    Clustered = 'clustered',
    Stacked = 'stacked',
    Stacked100p = 'stacked100%',
  }

  export class State extends ChartVisual.State { }
  export class Style extends ChartVisual.Style { }

  export interface Options extends ChartVisual.Options {
    readonly direction: BarDirection;
    readonly displayType: DisplayType;
  }

  export const roles = VisualFieldRoleTypeMap.create({
    axis: new VisualFieldRoleType('axis', {
      ...VisualFieldRoleType.Defaults.groupingAxis
    }),

    measures: new VisualFieldRoleType('measures', {
      ...VisualFieldRoleType.Defaults.measures,
      limits: v =>
        (v.options().displayType == DisplayType.Simple || v.fieldRoles.get(roles.legend).fields.length > 0)
          ? { min: 1, max: 1 }
          : { min: 1 }
    }),

    legend: new VisualFieldRoleType('legend', {
      ...VisualFieldRoleType.Defaults.detailLegend,
      isHidden: v => v.options().displayType == DisplayType.Simple,
      isEnabled: v => v.fieldRoles.get(roles.measures).fields.length <= 1,
      limits: v => ({ min: 0, max: 1 })
    }),
  });

  export const type = ChartVisual.defineType<BarChartVisualComponent>(
    BarChartVisualComponent,
    'barChart',
    roles,
    {
      visualOptions: {
        direction: BarDirection.Horizontal,
        displayType: DisplayType.Simple,
        dataLabelPosition: ChartVisual.DataLabelsPosition.Outside,

        legendEnabledByDefault: false,
        dataLabelsEnabledByDefault: false,
      },
      createState: () => new State(),
      createStyle: () => new Style(),
      showOverlayUntilRendered: true,
      getPbiChartType: v => {
        let opt = v.options() as Options;
        if (opt.direction == BarDirection.Vertical) {
          if (opt.displayType == DisplayType.Clustered)
            return PbiChartTypes.clusteredColumnChart;
          
          if (opt.displayType == DisplayType.Stacked)
            return PbiChartTypes.stackedColumnChart;
          
          if (opt.displayType == DisplayType.Stacked100p)
            return PbiChartTypes.hundredPercentStackedColumnChart;

          return PbiChartTypes.columnChart;
        }
        else {
          if (opt.displayType == DisplayType.Clustered)
            return PbiChartTypes.clusteredBarChart;
          
          if (opt.displayType == DisplayType.Stacked)
            return PbiChartTypes.stackedBarChart;
          
          if (opt.displayType == DisplayType.Stacked100p)
            return PbiChartTypes.hundredPercentStackedBarChart;

          return PbiChartTypes.barChart;
        }
      },

      percentageDisplayChart:
        v => (v.options() as Options)?.displayType == DisplayType.Stacked100p,
    });
}
